import { faImages } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, Card, CardActions, CardContent, CardMedia, Divider, Grid, Typography } from "@mui/material";
import dayjs from "dayjs";
import { FC, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { SiteEventType } from "../../api/dtos/dtos";
import AuthContext from "../../contexts/auth/AuthContext";
import MapContext, { MapFilterPeriodEnum, MapFilterSubmitter } from "../../contexts/data/MapContext";
import { SpeciesContext } from "../../contexts/data/SpeciesContext";
import useGetSiteEventGetCollectionGallery from "../../hooks/data/useGetSiteEventGetCollectionGallery";
import { AppSettings } from "../../AppSettings";
import isValidDateRange from "../../functions/date";

interface IPhotosCardProps {
    title: string,
    siteeventtype: SiteEventType
}

const PhotosCard: FC<IPhotosCardProps> = ({
    title,
    siteeventtype
}) => {
    //
    // -- Contexts --
    const { user } = useContext(AuthContext);
    const { filter, setMapCenter } = useContext(MapContext);
    const { species: {species} } = useContext(SpeciesContext);

    //
    // -- Hooks --
    const navigate = useNavigate();

    const isAdminOrSuperAdmin = useMemo(() => {
        return !!user?.roles?.find(x => x === 'admin' || x === 'superadmin');
    }, [user]);

    const isDataAccessForThisSpecies = useMemo(() => {
        return (
            user?.matchingRegionAdminRoles?.some(
                x => x.speciesId == null || x.speciesId === species
            ) ?? false
        );
    }, [user?.matchingRegionAdminRoles, species]);
    
    // Generate the photo gallery request based off map filter values
    const photoGalleryRequest = useMemo(() => {

        if (!isValidDateRange(filter.reportingPeriod.fromField, filter.reportingPeriod.toField)) {
            return undefined;
        }

        // Period
        let from = null;
        let to = null;
        switch (filter.reportingPeriod.period) {
            case MapFilterPeriodEnum.DateRange: 
                from = filter.reportingPeriod.fromField;
                to = filter.reportingPeriod.toField;
                break;

            case MapFilterPeriodEnum.Year: 
                const year = filter.reportingPeriod.yearField;
                from = year?.startOf('year');

                // If the year is equal to this year, we want to cap off
                // at the current date.
                if (year?.year() === dayjs().year()) {
                    to = dayjs();
                } else {
                    to = year?.endOf('year');
                }

                break;
                
            case MapFilterPeriodEnum.MonthYear: 
                const monthYear = filter.reportingPeriod.monthYearField;
                from = monthYear?.startOf('month');

                // If the year is equal to this year, we want to cap off
                // at the current date.
                if (monthYear?.year() === dayjs().year() && monthYear?.month() === dayjs().month()) {
                    to = dayjs();
                } else {
                    to = monthYear?.endOf('month');
                }

                break;

            case MapFilterPeriodEnum.LastSevenDays: 
                from = dayjs().subtract(7, 'days');  
                to = dayjs();  
                break;

            case MapFilterPeriodEnum.LastThirtyOneDays: 
                from = dayjs().subtract(31, 'days');  
                to = dayjs();  
                break;
        }

        switch (filter.bySubmitter.submitter) {
            case MapFilterSubmitter.AllData:
                return {
                    species: species, 
                    startDateOnOrAfter: from?.format('DD/MM/YYYY'),
                    startDateOnOrBefore: to?.format('DD/MM/YYYY'),
                    locatedInRegionIds: (!isAdminOrSuperAdmin && isDataAccessForThisSpecies) ? filter.bySubmitter.locatedInRegionIds : undefined,
                    dataAccessId: (!isAdminOrSuperAdmin && isDataAccessForThisSpecies) ? filter.bySubmitter.dataAccessId : undefined,
                };
            case MapFilterSubmitter.PublicData:
                return {
                    species: species,
                    onlyPublicData: true,
                    startDateOnOrAfter: from?.format('DD/MM/YYYY'),
                    startDateOnOrBefore: to?.format('DD/MM/YYYY')
                };
            case MapFilterSubmitter.MyData:
                return {
                    species: species,
                    userId: user?.id,
                    startDateOnOrAfter: from?.format('DD/MM/YYYY'),
                    startDateOnOrBefore: to?.format('DD/MM/YYYY')
                };
            case MapFilterSubmitter.MyGroup:
                return {
                    species: species,
                    userId: filter.bySubmitter.communityGroupMemberId != -1 ? filter.bySubmitter.communityGroupMemberId : undefined,
                    startDateOnOrAfter: from?.format('DD/MM/YYYY'),
                    startDateOnOrBefore: to?.format('DD/MM/YYYY'),
                    memberOfCommunityGroupIds: filter.bySubmitter.communityGroupId ? [filter.bySubmitter.communityGroupId] : undefined
                };
        }
    }, [user, filter, species]);

    // Gallery requests 
    const { data: galleryData } = useGetSiteEventGetCollectionGallery(
        {
            ...photoGalleryRequest,
            siteEventType: siteeventtype
        }, 
        { revalidateOnFocus: false, errorRetryCount: 0 }
    );

    const getImageLink = (galleryId: number | undefined, photoId: number | undefined, thumbnail: boolean) => {
        if (thumbnail){
            return `${AppSettings.baseUrl}/gallery/${galleryId}/photo/${photoId}/thumbnail`;
        }
        return `${AppSettings.baseUrl}/gallery/${galleryId}/photo/${photoId}`;
    };

    const handleOnClick = (latitude: string, longitude: string) => {
        setMapCenter({latitude: +latitude, longitude: +longitude, zoom: 22});
        navigate('../map');
    };

    return (
        <Card 
            variant='outlined'
            sx={{
                marginY: '15px'
            }}
        >
            <CardContent>
                <Grid container spacing={4} alignItems={"center"}>
                    <Grid item xs={12}>
                        <Box display="flex" alignItems={"center"}>
                            <FontAwesomeIcon icon={faImages} style={{ fontSize: '1.2rem', paddingRight: "5px" }} />
                            <Typography paddingLeft={"5px"} fontSize={24}>
                                {title} Photos
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>

                <Box sx={{padding: "10px"}} />

                {galleryData?.length === 0 && (
                    <Typography>No photos were found.</Typography>
                )}

                <Grid container spacing={2} alignItems={"center"}>
                    {galleryData?.map(image => 
                        <Grid
                            key={`photo-grid-${image?.galleryPhotosId}-${image?.siteEventId}`}
                            item
                            xs={12}
                            sm={6}
                            md={4}
                            lg={3}
                            xl={2}
                        >
                            <Card
                                sx={{
                                    width: '100%'
                                }}
                            >
                                <Box
                                    sx={{
                                        width: '100%',
                                        pb: '100%',
                                        position: 'relative',
                                        backgroundColor: 'grey.300'
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                '&:before': {
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    zIndex: 50,
                                                    display: 'block',
                                                    content: '\' \'',
                                                    backgroundColor: 'rgba(0,0,0, 0.15)'
                                                }
                                            }

                                        }}
                                        onClick={()=> window.open(getImageLink(image.galleryId, image.galleryPhotosId, false), "_blank")}
                                    >
                                        <CardMedia
                                            component="img"
                                            alt="Failed to load image."
                                            image={getImageLink(image.galleryId, image.galleryPhotosId, true)}
                                        />
                                    </Box>
                                </Box>
                                <Divider />
                                <CardActions>
                                    <Button 
                                        size="small"
                                        onClick={() => handleOnClick(image.latitude, image.longitude)}
                                    >
                                        Map view
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    )}
                </Grid>
            </CardContent>
        </Card>
    )
}

export default PhotosCard